import { Int } from '@adornis/baseql/baseqlTypes.js';
import { Arg, Entity, Mutation } from '@adornis/baseql/decorators.js';
import type { EntityData } from '@adornis/baseql/entities/types.js';
import { getRawCollection } from '@adornis/baseql/server/collections.js';
import { Counter } from '@adornis/counter/counter.js';
import { Mitarbeiter } from '@chancenwerk/master-data-management/users/entities/Mitarbeiter.js';
import type { LogistikOrder } from './LogistikOrder.js';

export enum CounterType {
  STUDENT_ID = 'studentID',
  EMPLOYEE_ID = 'employeeID',
  DEBITORENNUMMER = 'debitorennummer',
  LOGISTIK_ORDER_NUMBER = 'logistikOrderNumber',
}

@Entity()
export class ChancenwerkCounter extends Counter {
  static override _class = 'ChancenwerkCounter';
  public static override _collectionName = 'counters';

  @Mutation(type => Int)
  public static getIncrementingStudentID() {
    return async (gqlFields: void) => {
      const { Student } = await import('@chancenwerk/master-data-management/users/entities/Student');
      const studentCol = await getRawCollection(Student._collectionName);

      let studentID: number;
      let foundStudents: number;
      do {
        studentID = await this.getIncrementingNumber(CounterType.STUDENT_ID)();

        foundStudents = await studentCol.countDocuments(
          { studentID },
          { readConcern: 'linearizable', maxTimeMS: 20000 },
        );
      } while (foundStudents);
      return studentID;
    };
  }

  @Mutation(type => Int)
  public static getIncrementingEmployeeID() {
    return async (gqlFields: void) => {
      const mitarbeiterCol = await getRawCollection(Mitarbeiter._collectionName);

      let mitarbeiterID: number;
      let foundMitarbeiter: number;
      do {
        mitarbeiterID = await this.getIncrementingNumber(CounterType.EMPLOYEE_ID)();

        foundMitarbeiter = await mitarbeiterCol.countDocuments(
          { employeeID: mitarbeiterID },
          { readConcern: 'linearizable', maxTimeMS: 20000 },
        );
      } while (foundMitarbeiter);
      return mitarbeiterID;
    };
  }

  @Mutation(type => String)
  static getIncrementingLogistikOrderNumber() {
    return async () => {
      const { LogistikOrder } = await import('./LogistikOrder');
      const orderCol = await getRawCollection<EntityData<LogistikOrder>>(LogistikOrder._collectionName);

      const orderNumberFormatter = Intl.NumberFormat('de-DE', { minimumIntegerDigits: 8, useGrouping: false });

      let orderNumber: number;
      let foundOrders: number;
      do {
        orderNumber = await this.getIncrementingNumber(CounterType.LOGISTIK_ORDER_NUMBER)();

        foundOrders = await orderCol.countDocuments(
          { orderNumber: orderNumberFormatter.format(orderNumber) },
          { readConcern: 'linearizable', maxTimeMS: 20000 },
        );
      } while (foundOrders);

      return orderNumberFormatter.format(orderNumber);
    };
  }

  @Mutation(type => Int)
  public static getIncrementingStudentDebitorennummerByKostenstellennr(
    @Arg('kostenstellennr', type => String) kostenstellennr: string,
  ) {
    if (!kostenstellennr) throw new Error('schoolID missing');
    return async (gqlFields: void) => {
      const collection = await getRawCollection<ChancenwerkCounter>(this._collectionName);
      const counterDoc = await collection.findOneAndUpdate(
        { type: CounterType.DEBITORENNUMMER + '_' + kostenstellennr },
        { $inc: { counter: 1 } },
        { upsert: true, returnDocument: 'after' },
      );
      return counterDoc?.counter;
    };
  }

  @Mutation(type => Boolean)
  public static setStartingCounterValue(
    @Arg('startValue', type => Int) startValue: number,
    @Arg('counterType', type => String) counterType: CounterType,
  ) {
    return async (gqlFields: void) => {
      const collection = await getRawCollection<ChancenwerkCounter>(this._collectionName);
      const counter = await collection.findOneAndUpdate(
        { type: counterType },
        {
          $setOnInsert: { counter: startValue },
        },
        { upsert: true, returnDocument: 'after' },
      );
      if (!counter) throw new Error('Something went wrong when setting the counters starting value!');
      return true;
    };
  }
}
